import type { DayPoint, HourPoint, OutcomeStat, TextCount } from '@/shared/api/types'
import styles from './Stats.module.css'

/**
 * Графики нарисованы на SVG вручную.
 *
 * Библиотека вроде Recharts — это полторы сотни килобайт ради трёх
 * простых форм: столбики, полосы и сетка. Здесь их и есть три, а
 * взамен получается полный контроль над тем, как они выглядят: те же
 * токены темы, те же цвета состояний, никаких чужих умолчаний.
 */

/** Звонки по дням: удачные снизу, отказы сверху. */
export function DayChart({ points }: { points: DayPoint[] }) {
  if (points.length === 0) return <p className={styles.empty}>За этот период звонков не было</p>

  const max = Math.max(...points.map((p) => p.calls), 1)
  // Ширина в процентах: график тянется по контейнеру, а не задан в
  // пикселях — иначе на узком экране он обрежется.
  const step = 100 / points.length
  const barWidth = Math.min(step * 0.62, 6)

  return (
    <div className={styles.chart}>
      <svg viewBox="0 0 100 34" preserveAspectRatio="none" className={styles.chartSvg}>
        {/* Сетка: три линии, еле заметные. Без них столбики висят в
            пустоте и высоту не с чем сравнить. */}
        {[0, 0.5, 1].map((ratio) => (
          <line
            key={ratio}
            x1="0"
            x2="100"
            y1={30 - ratio * 28}
            y2={30 - ratio * 28}
            stroke="var(--border)"
            strokeWidth="0.25"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {points.map((point, i) => {
          const x = i * step + (step - barWidth) / 2
          const height = (point.calls / max) * 28
          const winHeight = point.calls > 0 ? (point.wins / point.calls) * height : 0

          return (
            <g key={point.date}>
              <title>{`${label(point.date)}: ${point.calls}, к делу ${point.wins}`}</title>
              <rect
                x={x}
                y={30 - height}
                width={barWidth}
                height={height}
                fill="var(--border-strong)"
                rx="0.6"
              />
              {winHeight > 0 && (
                <rect
                  x={x}
                  y={30 - winHeight}
                  width={barWidth}
                  height={winHeight}
                  fill="var(--success)"
                  rx="0.6"
                />
              )}
            </g>
          )
        })}
      </svg>

      <div className={styles.axis}>
        <span>{label(points[0]?.date ?? '')}</span>
        <span>{label(points[points.length - 1]?.date ?? '')}</span>
      </div>
    </div>
  )
}

/** Итоги полосами: самый частый сверху. */
export function OutcomeChart({ items }: { items: OutcomeStat[] }) {
  if (items.length === 0) return <p className={styles.empty}>Разобранных разговоров пока нет</p>

  const max = Math.max(...items.map((item) => item.count), 1)

  return (
    <div className={styles.bars}>
      {items.map((item) => (
        <div key={item.outcome} className={styles.bar}>
          <span className={styles.barLabel}>{item.label}</span>
          <span className={styles.barTrack}>
            <span
              className={`${styles.barFill} ${item.success ? styles.barWin : styles.barLoss}`}
              style={{ width: `${(item.count / max) * 100}%` }}
            />
          </span>
          <span className={styles.barValue}>{item.count}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Часы суток: сколько звонков и какая доля удачных.
 *
 * Столбик — количество, заливка внутри — удачные. Показываются только
 * часы, когда действительно звонили: двадцать четыре столбика, из
 * которых восемнадцать пустые, — это график про ночь.
 */
export function HourChart({ points }: { points: HourPoint[] }) {
  const active = points.filter((p) => p.calls > 0)
  if (active.length === 0) return <p className={styles.empty}>Данных по часам пока нет</p>

  const max = Math.max(...active.map((p) => p.calls), 1)

  return (
    <div className={styles.hours}>
      {active.map((point) => {
        const rate = point.calls > 0 ? point.wins / point.calls : 0
        return (
          <div
            key={point.hour}
            className={styles.hour}
            title={`${point.hour}:00 — ${point.calls} звонков, к делу ${point.wins}`}
          >
            <span className={styles.hourBar}>
              <span
                className={styles.hourFill}
                style={{ height: `${(point.calls / max) * 100}%` }}
              >
                <span className={styles.hourWins} style={{ height: `${rate * 100}%` }} />
              </span>
            </span>
            <span className={styles.hourLabel}>{point.hour}</span>
          </div>
        )
      })}
    </div>
  )
}

/** Повторяющиеся строки: возражения, обещания, промахи. */
export function TextList({ items, empty }: { items: TextCount[]; empty: string }) {
  if (items.length === 0) return <p className={styles.empty}>{empty}</p>

  return (
    <div className={styles.texts}>
      {items.map((item) => (
        <div key={item.text} className={styles.textRow}>
          <span className={styles.textValue}>{item.text}</span>
          {/* Число повторов моноширинным и справа: по нему сравнивают
              строки между собой. */}
          <span className={styles.textCount}>{item.count}</span>
        </div>
      ))}
    </div>
  )
}

function label(date: string): string {
  if (!date) return ''
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return date
  return parsed.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}
